class Header2026 extends HTMLElement {
  constructor() {
    super();
    this.openMega = null;
    this.drawerOpen = false;
    this.openDelay = 60;
    this.closeDelay = 220;
    this._openTimer = null;
    this._closeTimer = null;
  }

  connectedCallback() {
    this.triggers = this.querySelectorAll('[data-h26-trigger]');
    this.megas = this.querySelectorAll('[data-h26-mega]');
    this.burger = this.querySelector('[data-h26-burger]');
    this.drawer = this.querySelector('[data-h26-drawer]');
    this.overlay = this.querySelector('[data-h26-overlay]');
    this.navBar = this.querySelector('.h26-nav-bar');

    this.triggers.forEach((t) => {
      t.addEventListener('click', this.onTriggerClick.bind(this));
      t.addEventListener('mouseenter', this.onTriggerEnter.bind(this));
      t.addEventListener('mouseleave', this.onTriggerLeave.bind(this));
      t.addEventListener('focus', this.onTriggerEnter.bind(this));
    });

    this.megas.forEach((m) => {
      m.addEventListener('mouseenter', this.cancelClose.bind(this));
      m.addEventListener('mouseleave', this.scheduleClose.bind(this));
    });

    if (this.burger) {
      this.burger.addEventListener('click', this.toggleDrawer.bind(this));
    }
    // de17: drawer is full-screen now so the burger sits behind it. The close
    // button is delegated (not bound directly) because ensurePanels() replaces
    // the drawer's innerHTML — a direct binding would be lost in the swap.
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-h26-drawer-close]')) this.closeDrawer();
    });
    if (this.overlay) {
      this.overlay.addEventListener('click', this.closeAll.bind(this));
    }

    // SSR ships slim panels (plain link lists, crawlable); the rich markup is
    // fetched on first interaction intent. Prefetch listeners are {once} —
    // failures are retried by the ensurePanels() calls in the open paths.
    if (this.dataset.panels === 'slim') {
      const prefetch = () => this.ensurePanels();
      this.navBar?.addEventListener('pointerover', prefetch, { once: true });
      this.navBar?.addEventListener('focusin', prefetch, { once: true });
      this.burger?.addEventListener('pointerdown', prefetch, { once: true });
    }

    document.addEventListener('keydown', this.onKey.bind(this));
    document.addEventListener('click', this.onDocClick.bind(this));

    // Track link clicks inside mega-menus and mobile drawer
    this.addEventListener('click', (e) => {
      const link = e.target.closest('a[href]');
      if (!link) return;
      const mega = link.closest('[data-h26-mega]');
      const inDrawer = link.closest('[data-h26-drawer]');
      if (!mega && !inDrawer) return;
      const menu = mega?.id || 'mobile_drawer';
      const device = inDrawer ? 'mobile' : 'desktop';
      document.dispatchEvent(new CustomEvent('nav:link-clicked', {
        detail: { menu, device, url: link.getAttribute('href'), label: link.textContent?.trim() || '' },
      }));
    });
  }

  isDesktop() {
    return window.matchMedia('(min-width: 1101px)').matches;
  }

  /**
   * Swaps the slim SSR panels for the rich render via the Section Rendering
   * API (the same section renders rich when section.index is nil). Container
   * elements ([data-h26-mega], [data-h26-drawer]) keep their identity — only
   * inner content is replaced — so open state, aria attributes, and
   * container-level listeners all survive. Single-flight; resets on failure
   * so the next open retries. The slim panels remain a working fallback.
   */
  ensurePanels() {
    if (this.dataset.panels !== 'slim') return Promise.resolve();
    if (this._panelsPromise) return this._panelsPromise;
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return Promise.resolve();
    const root = window.Shopify?.routes?.root || '/';
    this._panelsPromise = fetch(`${root}?section_id=${encodeURIComponent(sectionId)}`)
      .then((r) => {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        doc.querySelectorAll('[data-h26-mega]').forEach((remote) => {
          const local = this.querySelector('#' + CSS.escape(remote.id));
          if (local) local.innerHTML = remote.innerHTML;
        });
        const remoteDrawer = doc.querySelector('[data-h26-drawer]');
        if (remoteDrawer && this.drawer) this.drawer.innerHTML = remoteDrawer.innerHTML;
        this.dataset.panels = 'rich';
      })
      .catch((err) => {
        console.warn('[header-2026] rich panel fetch failed — slim links remain usable:', err);
        this._panelsPromise = null;
      });
    return this._panelsPromise;
  }

  onTriggerClick(e) {
    if (this.isDesktop()) {
      e.preventDefault();
      const target = e.currentTarget.dataset.h26Trigger;
      if (this.openMega === target) {
        this.closeMega();
      } else {
        this.openMegaById(target, e.currentTarget);
      }
    }
  }

  onTriggerEnter(e) {
    if (!this.isDesktop()) return;
    const target = e.currentTarget.dataset.h26Trigger;
    this.cancelClose();
    if (this.openMega === target) return;
    clearTimeout(this._openTimer);
    this._openTimer = setTimeout(() => {
      this.openMegaById(target, e.currentTarget);
    }, this.openDelay);
  }

  onTriggerLeave(e) {
    if (!this.isDesktop()) return;
    clearTimeout(this._openTimer);
    const related = e.relatedTarget;
    if (related && related.closest && (related.closest('[data-h26-mega]') || related.closest('[data-h26-trigger]'))) return;
    this.scheduleClose();
  }

  scheduleClose() {
    if (!this.isDesktop()) return;
    clearTimeout(this._closeTimer);
    this._closeTimer = setTimeout(() => this.closeMega(), this.closeDelay);
  }

  cancelClose() {
    clearTimeout(this._closeTimer);
  }

  // aria-hidden alone leaves a panel's links Tab-reachable, which axe flags
  // (aria-hidden-focus). Pair every aria-hidden toggle with the matching
  // `inert` attribute so closed panels are also removed from the tab order.
  setMegaHidden(el, hidden) {
    if (!el) return;
    el.dataset.open = hidden ? 'false' : 'true';
    el.setAttribute('aria-hidden', hidden ? 'true' : 'false');
    if (hidden) {
      el.setAttribute('inert', '');
    } else {
      el.removeAttribute('inert');
    }
  }

  openMegaById(id, triggerEl) {
    this.ensurePanels();
    this.cancelClose();
    if (this.openMega && this.openMega !== id) {
      const prev = this.querySelector('#' + CSS.escape(this.openMega));
      this.setMegaHidden(prev, true);
    }
    const mega = this.querySelector('#' + CSS.escape(id));
    if (!mega) return;
    this.setMegaHidden(mega, false);
    this.triggers.forEach((t) => {
      const isActive = t.dataset.h26Trigger === id;
      t.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    });
    this.openMega = id;
    this.dataset.megaOpen = '';
    document.dispatchEvent(new CustomEvent('nav:menu-opened', {
      detail: { menu: id, label: triggerEl?.textContent?.trim() || id, device: 'desktop' },
    }));
  }

  closeMega() {
    if (this.openMega) {
      const prev = this.querySelector('#' + CSS.escape(this.openMega));
      this.setMegaHidden(prev, true);
    }
    this.triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
    this.openMega = null;
    delete this.dataset.megaOpen;
  }

  toggleDrawer() {
    if (this.drawerOpen) this.closeDrawer();
    else this.openDrawer();
  }

  openDrawer() {
    if (!this.drawer) return;
    this.ensurePanels();
    this.drawer.dataset.open = 'true';
    this.drawer.setAttribute('aria-hidden', 'false');
    this.drawer.removeAttribute('inert');
    this.burger.setAttribute('aria-expanded', 'true');
    this.drawerOpen = true;
    document.body.style.overflow = 'hidden';
    if (this.overlay) this.overlay.dataset.active = 'true';
    document.dispatchEvent(new CustomEvent('nav:menu-opened', {
      detail: { menu: 'mobile_drawer', label: 'Mobile menu', device: 'mobile' },
    }));
  }

  closeDrawer() {
    if (!this.drawer) return;
    this.drawer.dataset.open = 'false';
    this.drawer.setAttribute('aria-hidden', 'true');
    this.drawer.setAttribute('inert', '');
    this.burger.setAttribute('aria-expanded', 'false');
    this.drawerOpen = false;
    document.body.style.overflow = '';
    if (this.overlay) this.overlay.dataset.active = 'false';
  }

  closeAll() {
    this.closeMega();
    this.closeDrawer();
    if (this.overlay) this.overlay.dataset.active = 'false';
  }

  onKey(e) {
    if (e.key === 'Escape') this.closeAll();
  }

  onDocClick(e) {
    if (!this.openMega) return;
    if (e.target.closest('[data-h26-trigger]')) return;
    if (e.target.closest('[data-h26-mega]')) return;
    this.closeMega();
  }
}

if (!customElements.get('header-2026')) {
  customElements.define('header-2026', Header2026);
}
